import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { CheckCircle, Filter, XCircle } from 'lucide-react'
import { useEffect } from 'react'
import { AdminDataGrid } from '@/components/grid/AdminDataGrid'
import { PaginationControls } from '@/components/grid/PaginationControls'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDateTime } from '@/lib/utils'
import { useSystemStore } from '@/stores/system-store'

function formatDuration(ms: number | null): string {
  if (ms === null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

type CronRow = ReturnType<typeof useSystemStore.getState>['cronExecutions'][number]
type CronCellParams = ICellRendererParams<CronRow>

export function CronLog() {
  useAuth()
  const {
    cronExecutions,
    cronPagination,
    isLoadingCronExecutions,
    cronNameFilter,
    setCronNameFilter,
    fetchCronExecutions,
  } = useSystemStore()

  useEffect(() => {
    fetchCronExecutions()
  }, [fetchCronExecutions])

  const cronNames = [
    { value: '', label: 'All Jobs' },
    { value: 'alert_checks', label: 'Alert Checks' },
    { value: 'waitlist_cleanup', label: 'Waitlist Cleanup' },
  ]

  const executionColumns: Array<ColDef<CronRow>> = [
    {
      field: 'cronName',
      headerName: 'Job',
      flex: 1,
      minWidth: 170,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] font-medium text-ink">
            {params.data.cronName.replace(/_/g, ' ')}
          </span>
        ) : null,
    },
    {
      field: 'scheduledAt',
      headerName: 'Scheduled At',
      minWidth: 180,
      maxWidth: 210,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] tabular-nums text-ink-muted">
            {formatDateTime(params.data.scheduledAt)}
          </span>
        ) : null,
    },
    {
      field: 'durationMs',
      headerName: 'Duration',
      minWidth: 110,
      maxWidth: 130,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] tabular-nums text-ink-muted">
            {formatDuration(params.data.durationMs)}
          </span>
        ) : null,
    },
    {
      field: 'success',
      headerName: 'Status',
      minWidth: 110,
      maxWidth: 120,
      cellRenderer: (params: CronCellParams) => {
        const data = params.data
        if (!data) return null
        return data.success ? (
          <div className="flex items-center gap-1.5">
            <CheckCircle className="h-3 w-3 text-positive" strokeWidth={1.5} />
            <span className="font-mono text-[10px] text-positive">OK</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <XCircle className="h-3 w-3 text-negative" strokeWidth={1.5} />
            <span className="font-mono text-[10px] text-negative">FAIL</span>
          </div>
        )
      },
    },
    {
      field: 'itemsProcessed',
      headerName: 'Items',
      minWidth: 90,
      maxWidth: 110,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] tabular-nums text-ink-muted">
            {params.data.itemsProcessed ?? 0}
          </span>
        ) : null,
    },
    {
      field: 'startedAt',
      headerName: 'Ran At',
      minWidth: 180,
      maxWidth: 210,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] tabular-nums text-ink-muted">
            {formatDateTime(params.data.startedAt)}
          </span>
        ) : null,
    },
    {
      field: 'errors',
      headerName: 'Errors',
      flex: 1.2,
      minWidth: 220,
      cellRenderer: (params: CronCellParams) =>
        params.data ? (
          <span
            className={cn(
              'block truncate font-mono text-[10px]',
              params.data.errors ? 'text-negative' : 'text-ink-faint'
            )}
            title={params.data.errors ?? '—'}
          >
            {params.data.errors ?? '—'}
          </span>
        ) : null,
    },
  ]

  return (
    <AppShell title="Cron Execution Log">
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3">
          <Filter className="h-3.5 w-3.5 text-ink-faint" strokeWidth={1.5} />
          <div className="flex rounded-md border border-dashed border-border bg-canvas">
            {cronNames.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setCronNameFilter(item.value)}
                className={cn(
                  'px-3 py-1.5 font-mono text-[10px] transition-colors first:rounded-l-md last:rounded-r-md',
                  cronNameFilter === item.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <p className="ml-auto font-mono text-[10px] text-ink-faint">
            {cronPagination.total} total executions
          </p>
        </div>

        <AdminDataGrid
          columns={executionColumns}
          emptyMessage="No cron executions recorded yet"
          getRowClass={({ data }) => (!data?.success ? 'lookbook-grid-row-error' : undefined)}
          getRowId={({ data }) => data.id}
          loading={isLoadingCronExecutions}
          rows={cronExecutions}
        />

        <PaginationControls
          label="executions"
          onNext={() => fetchCronExecutions(cronPagination.page + 1)}
          onPrevious={() => fetchCronExecutions(cronPagination.page - 1)}
          page={cronPagination.page}
          total={cronPagination.total}
          totalPages={cronPagination.totalPages}
        />
      </div>
    </AppShell>
  )
}
